import React, { useState, useEffect, useRef } from 'react';
import { useTranslation, getLanguageFromUrl } from '../../utils/languageUtils';

const NormalExportPanel = ({
  exportedImages,
  showExportedImages,
  setShowExportedImages,
  allViewsImages = [],
  onAddToCart,
  isTestMode = true,
  isExporting = false,
  statusMessage = ''
}) => {
  const [currentViewIndex, setCurrentViewIndex] = useState(0);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [zoomLabel, setZoomLabel] = useState('');
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomX, setZoomX] = useState(0);
  const [zoomY, setZoomY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const zoomContainerRef = useRef(null);
  const zoomImgRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setZoomSrc(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const openZoom = (src, label) => {
    setZoomSrc(src);
    setZoomLabel(label);
    setZoomScale(1);
    setZoomX(0);
    setZoomY(0);
  };

  const onWheelZoom = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoomScale(s => Math.max(0.5, Math.min(8, s + delta)));
  };

  const onMouseDown = (e) => {
    setIsPanning(true);
    lastPosRef.current = { x: e.clientX, y: e.clientY };
  };
  const onMouseMove = (e) => {
    if (!isPanning) return;
    const dx = e.clientX - lastPosRef.current.x;
    const dy = e.clientY - lastPosRef.current.y;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setZoomX(x => x + dx);
    setZoomY(y => y + dy);
  };
  const onMouseUp = () => setIsPanning(false);
  const onMouseLeave = () => setIsPanning(false);

  const onTouchStart = (e) => {
    const t = e.touches[0];
    if (!t) return;
    setIsPanning(true);
    lastPosRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchMove = (e) => {
    const t = e.touches[0];
    if (!t || !isPanning) return;
    const dx = t.clientX - lastPosRef.current.x;
    const dy = t.clientY - lastPosRef.current.y;
    lastPosRef.current = { x: t.clientX, y: t.clientY };
    setZoomX(x => x + dx);
    setZoomY(y => y + dy);
  };
  const onTouchEnd = () => setIsPanning(false);

  const zoomIn = () => setZoomScale(s => Math.min(8, s * 1.2));
  const zoomOut = () => setZoomScale(s => Math.max(0.5, s / 1.2));
  const zoomReset = () => { setZoomScale(1); setZoomX(0); setZoomY(0); };
  const zoomFit = () => {
    const cont = zoomContainerRef.current;
    const img = zoomImgRef.current;
    if (!cont || !img) return;
    const cw = cont.clientWidth;
    const ch = cont.clientHeight;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    if (cw && ch && iw && ih) {
      const scale = Math.min(cw / iw, ch / ih);
      setZoomScale(Math.max(0.5, Math.min(8, scale)));
      setZoomX(0);
      setZoomY(0);
    }
  };

  if (!showExportedImages) return null;

  const hasAnyViews = Array.isArray(allViewsImages) && allViewsImages.length > 0;
  const hasMultipleViews = Array.isArray(allViewsImages) && allViewsImages.length > 1;
  const safeIndex = hasAnyViews ? Math.max(0, Math.min(currentViewIndex, allViewsImages.length - 1)) : 0;
  const currentImages = hasAnyViews ? allViewsImages[safeIndex] : exportedImages;

  const goToPreviousView = () => {
    setCurrentViewIndex(prev => (prev > 0 ? prev - 1 : allViewsImages.length - 1));
  };

  const goToNextView = () => {
    setCurrentViewIndex(prev => (prev < allViewsImages.length - 1 ? prev + 1 : 0));
  };

  const getViewIdRaw = (view) => {
    if (!view) return 'default';
    if (typeof view === 'string') return view;
    if (typeof view === 'object' && view.id) return view.id;
    return String(view);
  };

  const getViewDisplayName = (view) => {
    const lang = getLanguageFromUrl();
    const known = new Set(['front','back','left','right','top','bottom','default']);

    if (view && typeof view === 'object') {
      if (view.name) {
        if (typeof view.name === 'object') {
          return view.name[lang] || view.name.en || view.name.cn || view.id || '';
        }
        if (typeof view.name === 'string') {
          return view.name;
        }
      }
      if (view.id) {
        const key = String(view.id).toLowerCase();
        if (known.has(key)) return t(`views.${key}`);
        return view.id;
      }
    }

    const raw = getViewIdRaw(view);
    const key = (raw || 'default').toString().toLowerCase();
    if (known.has(key)) return t(`views.${key}`);
    return raw;
  };

  const getCurrentViewName = () => {
    if (hasAnyViews) {
      const view = allViewsImages[safeIndex]?.view;
      return getViewDisplayName(view) || `View ${safeIndex + 1}`;
    }
    return getViewDisplayName(exportedImages?.view) || t('views.default');
  };

  const handleDownloadCurrentView = () => {
    if (!currentImages) return;

    const downloadImage = (dataUrl, filename) => {
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const dateStr = new Date().toISOString().slice(0, 10);
    const viewName = getViewIdRaw(currentImages.view) || 'view';
    const productName = 'product';

    if (currentImages.preview) {
      downloadImage(currentImages.preview, `${productName}_${viewName}_preview-${dateStr}.png`);
    }

    if (currentImages.design) {
      downloadImage(currentImages.design, `${productName}_${viewName}_design-${dateStr}.png`);
    }
  };

  

  return (
    <>
      <style>{`@keyframes exported-spin{to{transform:rotate(360deg)}}`}</style>

      {isExporting && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'var(--site-muted)',
            zIndex: 1000001
          }}
          aria-hidden="true"
        />
      )}

      <div className="exported-images-panel" style={{
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '95%',
        maxWidth: '1200px',
        maxHeight: '95vh',
        backgroundColor: 'var(--site-bg)',
        borderRadius: '10px',
        padding: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
        zIndex: 1000002,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: 'var(--site-text)' }}>{t('export.title')}</h3>
          <button 
            onClick={() => setShowExportedImages(false)}
            style={{
              background: 'var(--site-muted)',
              border: '1px solid var(--site-border)',
              borderRadius: '50%',
              fontSize: '22px',
              fontWeight: 'bold',
              color: 'var(--site-text)',
              cursor: 'pointer',
              padding: '6px 10px',
              minWidth: '40px',
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '10px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
            aria-label={t('export.close')}
          >
            ×
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px' }}>
          {hasMultipleViews && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '12px',
              backgroundColor: 'var(--site-muted)',
              padding: '8px',
              borderRadius: '8px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={goToPreviousView}
                style={{
                  background: 'var(--site-bg)',
                  border: '1px solid var(--site-border)',
                  borderRadius: '4px',
                  padding: '5px 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  color: 'var(--site-text)'
                }}
              >
                ←
              </button>

              <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--site-text)' }}>
                {getCurrentViewName()} ({currentViewIndex + 1}/{allViewsImages.length})
              </div>

              <button
                onClick={goToNextView}
                style={{
                  background: 'var(--site-bg)',
                  border: '1px solid var(--site-border)',
                  borderRadius: '4px',
                  padding: '5px 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  color: 'var(--site-text)'
                }}
              >
                →
              </button>

              {isTestMode && (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={handleDownloadCurrentView}
                    style={{
                      background: '#4CAF50',
                      border: '1px solid #388E3C',
                      borderRadius: '4px',
                      padding: '5px 10px',
                      cursor: 'pointer',
                      color: 'white',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px'
                    }}
                  >
                    {t('export.downloadView')}
                  </button>
                </div>
              )}
            </div>
          )}

          <div style={{ 
            display: 'flex', 
            flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
            gap: '12px',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            padding: '8px 0'
          }}>
            <div style={{ textAlign: 'center', flex: 1, width: '100%' }}>
            <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', fontSize: '14px', color: 'var(--site-text)' }}>{t('export.preview')}</p>
              {currentImages && currentImages.preview ? (
                <img 
                  src={currentImages.preview} 
                  alt="Preview" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: window.innerWidth <= 480 ? '40vh' : '60vh', 
                    border: '1px solid var(--site-border)',
                    borderRadius: '4px',
                    objectFit: 'contain',
                    cursor: 'zoom-in'
                  }} 
                  onClick={() => { openZoom(currentImages.preview, t('export.preview')); }}
                />
              ) : (
                <div style={{ 
                  width: '100%', 
                  height: window.innerWidth <= 480 ? '40vh' : '60vh', 
                backgroundColor: 'var(--site-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                borderRadius: '4px',
                color: 'var(--site-text)'
              }}>
                  {t('export.noPreview')}
                </div>
              )}
            </div>

            <div style={{ textAlign: 'center', flex: 1, width: '100%' }}>
            <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', fontSize: '14px', color: 'var(--site-text)' }}>{t('export.design')}</p>
              {currentImages && currentImages.design ? (
                <img 
                  src={currentImages.design} 
                  alt="Design" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: window.innerWidth <= 480 ? '40vh' : '60vh', 
                    border: '1px solid var(--site-border)',
                    borderRadius: '4px',
                    objectFit: 'contain',
                    cursor: 'zoom-in'
                  }} 
                  onClick={() => { openZoom(currentImages.design, t('export.design')); }}
                />
              ) : (
                <div style={{ 
                  width: '100%', 
                  height: window.innerWidth <= 480 ? '40vh' : '60vh', 
                backgroundColor: 'var(--site-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                borderRadius: '4px',
                color: 'var(--site-text)'
              }}>
                  {t('export.noDesign')}
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '10px',
          paddingTop: '12px',
        borderTop: '1px solid var(--site-border)',
        background: 'var(--site-bg)'
      }}>
          {typeof onAddToCart === 'function' && (
            <button
              onClick={onAddToCart}
              disabled={isExporting}
              style={{
              background: 'var(--site-bg)',
              border: '1px solid var(--site-border)',
                borderRadius: '6px',
                padding: '10px 20px',
                cursor: isExporting ? 'not-allowed' : 'pointer',
              color: 'var(--site-text)',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                minWidth: '200px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
              }}
              aria-disabled={isExporting}
            >
              {t('export.addToCart')}
            </button>
          )}
        </div>

        {isExporting && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          background: 'var(--site-bg)',
          opacity: 0.6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            zIndex: 100
          }}>
            <div style={{
              width: 40,
              height: 40,
            border: '4px solid var(--site-border)',
            borderTopColor: 'var(--site-text)',
              borderRadius: '50%',
              animation: 'exported-spin 1s linear infinite'
            }} />
          <div style={{ fontWeight: 'bold', color: 'var(--site-text)' }}>{statusMessage || t('status.preparingExport')}</div>
          </div>
        )}
      </div>

      {zoomSrc && (
        <div style={{
          position: 'fixed',
          inset: 0,
        background: 'var(--site-muted)',
          zIndex: 1000003,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} onClick={() => setZoomSrc(null)}>
          <div style={{
            maxWidth: '95vw',
            maxHeight: '95vh',
          background: 'var(--site-bg)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            padding: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 8
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 700, color: 'var(--site-text)' }}>{zoomLabel}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={zoomOut} style={{
                background: 'var(--site-bg)',
                border: '1px solid var(--site-border)',
                borderRadius: 6,
                padding: '6px 12px',
                color: 'var(--site-text)',
                fontWeight: 500,
                cursor: 'pointer'
              }}>−</button>
              <button onClick={zoomIn} style={{
                background: 'var(--site-bg)',
                border: '1px solid var(--site-border)',
                borderRadius: 6,
                padding: '6px 12px',
                color: 'var(--site-text)',
                fontWeight: 500,
                cursor: 'pointer'
              }}>+</button>
              <button onClick={zoomReset} style={{
                background: 'var(--site-bg)',
                border: '1px solid var(--site-border)',
                borderRadius: 6,
                padding: '6px 12px',
                color: 'var(--site-text)',
                fontWeight: 500,
                cursor: 'pointer'
              }}>100%</button>
              <button onClick={zoomFit} style={{
                background: 'var(--site-bg)',
                border: '1px solid var(--site-border)',
                borderRadius: 6,
                padding: '6px 12px',
                color: 'var(--site-text)',
                fontWeight: 500,
                cursor: 'pointer'
              }}>Fit</button>
              <button onClick={() => setZoomSrc(null)} style={{
                background: 'var(--site-muted)',
                border: '1px solid var(--site-border)',
                borderRadius: '50%',
                width: 36,
                height: 36,
                fontSize: 18,
                cursor: 'pointer'
              }}>×</button>
              </div>
            </div>
            <div
              ref={zoomContainerRef}
              onWheel={onWheelZoom}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseLeave}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              style={{
                width: '100%',
                height: '80vh',
                overflow: 'hidden',
                cursor: isPanning ? 'grabbing' : zoomScale > 1 ? 'grab' : 'default'
              }}
            >
              <img
                ref={zoomImgRef}
                src={zoomSrc}
                alt="Zoom"
                style={{
                  transform: `translate(${zoomX}px, ${zoomY}px) scale(${zoomScale})`,
                  transformOrigin: 'center center',
                  display: 'block',
                  margin: '0 auto',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NormalExportPanel;
